/**
 * Authentication API functions - restructured from JS to TS
 *
 * @fileoverview Core authentication API functions for OTP-based user verification.
 * These functions handle the two-step authentication process: sending OTP to user's
 * phone number and verifying the entered OTP code.
 */
import { useMutation } from '@tanstack/react-query';
import {
  AcceptInviteParams,
  SendOtpParams,
  VerifyOtpParams,
} from 'lib/api/requests/auth/auth.requests.types';
import iotApi from '../../api/iotApi.client';
import { AuthQueryKey } from './auth.hooks.keys';

/**
 * Accepts user invitation and completes account setup
 *
 * @async
 * @function acceptInvite
 * @param {AcceptInviteParams} params - Parameters for accepting invitation
 * @param {number} params.id - Unique invitation ID
 * @param {string} params.email - User's email address (must match invitation)
 * @param {string} params.firstname - User's first name
 * @param {string} params.lastname - User's last name
 * @param {string} params.companyname - Company/organization name
 * @param {string} params.password - User's chosen password
 * @param {string} [params.phone] - Optional phone number for the user
 *
 * @returns {Promise<AcceptInviteResponse>} Promise that resolves to API response with account creation status
 *
 * @throws {Error} Throws error if invitation is invalid, expired, or API request fails
 *
 * @description
 * This function handles the invitation acceptance flow where:
 * 1. User receives an invitation email with a unique link/ID
 * 2. User fills out their profile information and sets password
 * 3. System validates the invitation and creates the user account
 * 4. User gains access to the platform with appropriate permissions
 *
 * @example
 * ```typescript
 * const result = await acceptInvite({
 *   id: 12345,
 *   email: 'user@company.com',
 *   firstname: 'John',
 *   lastname: 'Doe',
 *   companyname: 'Acme Corp',
 *   password: 'securePassword123',
 *   phone: '1234567890' // optional
 * });
 * ```
 *
 * @since Migration to TypeScript
 * @see {@link useAcceptInvite} - React Query hook for this API
 */

export function useAcceptInvite() {
  return useMutation({
    mutationKey: [AuthQueryKey.ACCEPT_INVITE],
    mutationFn: async (params: AcceptInviteParams) => {
      const res = await iotApi.auth.acceptInvite(params);

      return res;
    },
  });
}

/**
 * Sends OTP (One-Time Password) to user's registered phone number
 *
 * @async
 * @function sendOtp
 * @param {SendOtpParams} params - Parameters for sending OTP
 * @param {string} params.phone - User's phone number
 * @param {string} params.countryCode - Country code for the phone number
 * @param {number} params.userId - Unique identifier for the user
 *
 * @returns {Promise<SendOtpResponse>} Promise that resolves to API response containing OTP send status
 *
 * @throws {Error} Throws error if API request fails or response validation fails
 *
 * @example
 * ```typescript
 * const result = await sendOtp({
 *   phone: '1234567890',
 *   countryCode: '1',
 *   userId: 12345
 * });
 * ```
 *
 * @since Migration to TypeScript
 */
export function useSendOtp() {
  return useMutation({
    mutationKey: [AuthQueryKey.SEND_OTP],
    mutationFn: async (params: SendOtpParams) => {
      const { userId } = params;
      const res = await iotApi.auth.sendOtp({
        userId,
      });

      return res;
    },
  });
}

/**
 * Verifies the OTP entered by user against the one sent to their phone
 *
 * @async
 * @function verifyOtp
 * @param {VerifyOtpParams} params - Parameters for OTP verification
 * @param {string} params.phone - User's phone number (must match the one OTP was sent to)
 * @param {string} params.countryCode - Country code for the phone number
 * @param {number} params.userId - Unique identifier for the user
 * @param {string} params.otp - 6-digit OTP code entered by user
 *
 * @returns {Promise<VerifyOtpResponse>} Promise that resolves to API response with verification status and auth tokens
 *
 * @throws {Error} Throws error if OTP is invalid, expired, or API request fails
 *
 * @example
 * ```typescript
 * const result = await verifyOtp({
 *   phone: '1234567890',
 *   countryCode: '1',
 *   userId: 12345,
 *   otp: '123456'
 * });
 * ```
 *
 * @since Migration to TypeScript
 */

export function useVerifyOtp() {
  return useMutation({
    mutationKey: [AuthQueryKey.VERIFY_OTP],
    mutationFn: async (params: VerifyOtpParams) => {
      const { phone, userId, countryCode, otp } = params;
      const res = await iotApi.auth.verifyOtp({
        phone,
        countryCode,
        userId,
        otp,
      });

      return res;
    },
  });
}
